import { createReducer, on } from '@ngrx/store';
import { OrganizationChannel, ConfiguredChannel, DistributionList, Template, EverbridgeTemplate } from './communications.model';
import * as commActions from './communications.actions';
import { Document } from 'src/app/modules/core/store/models/document.model';
import { QueryParams } from 'src/app/modules/core/store/models/query.model';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';

export const communicationsFeatureKey = 'communications';

export interface State {
  organizationChannels: OrganizationChannelState;
  configuredChannels: ConfiguredChannelState;
  distributionLists: DistributionListState;
  templates: TemplateState;
  dataset: Dataset;
}

export interface OrganizationChannelState {
  queryParams?: QueryParams;
  organizationChannels: OrganizationChannel[];
  ui?: {};
}

export interface ConfiguredChannelState {
  queryParams?: QueryParams;
  configuredChannels: ConfiguredChannel[];
  selectedConfiguredChannel: ConfiguredChannel;
  selectedConfiguredChannelToDelete: ConfiguredChannel;
  slackChannelsForConfiguredChannel: any[];
  ui?: {
    topoEmailEditIsActive: boolean;
    sendGridEmailEditIsActive: boolean;
    slackEditIsActive: boolean;
    everbridgeEditIsActive: boolean;
    microsoftTeamsEditIsActive: boolean;
    twilioEditIsActive: boolean;
    isLoading: boolean;
  };
}

export interface DistributionListState {
  queryParams?: QueryParams;
  distributionLists: DistributionList[];
  selectedDistributionList: DistributionList;
  selectedDistributionListToDelete: DistributionList;
  dataQueryParams?: QueryParams;
  dataDocuments: Document[];
  ui?: {
    distributionListNewIsActive: boolean;
  };
}

export interface TemplateState {
  queryParams?: QueryParams;
  templates: Template[];
  everbridgeTemplates: EverbridgeTemplate[];
  selectedTemplate: Template;
  selectedTemplateToDelete: Template;
  selectedTemplateToCopy: Template;
  ui?: {
    templateNewIsActive: boolean;
    templateCopyIsActive: boolean;
  };
}

export const initialState: State = {
  organizationChannels: {
    queryParams: {
      skip: 0,
      take: 100,
      totalCount: 0,
      sortField: 'Name',
      sortDirection: 'Ascending'
    },
    organizationChannels: [],
    ui: {}
  },
  configuredChannels: {
    queryParams: {
      skip: 0,
      take: 10,
      totalCount: 0,
      sortField: 'Name',
      sortDirection: 'Ascending'
    },
    configuredChannels: [],
    selectedConfiguredChannel: null,
    selectedConfiguredChannelToDelete: null,
    slackChannelsForConfiguredChannel: [],
    ui: {
      topoEmailEditIsActive: false,
      sendGridEmailEditIsActive: false,
      slackEditIsActive: false,
      everbridgeEditIsActive: false,
      microsoftTeamsEditIsActive: false,
      twilioEditIsActive: false,
      isLoading: false
    }
  },
  distributionLists: {
    queryParams: {
      skip: 0,
      take: 10,
      totalCount: 0,
      sortField: 'Name',
      sortDirection: 'Ascending'
    },
    dataQueryParams: {
      skip: 0,
      take: 10,
      totalCount: 0,
      sortField: null,
      sortDirection: 'Ascending'
    },
    dataDocuments: [],
    distributionLists: [],
    selectedDistributionList: null,
    selectedDistributionListToDelete: null,
    ui: {
      distributionListNewIsActive: false
    }
  },
  templates: {
    queryParams: {
      skip: 0,
      take: 10,
      totalCount: 0,
      sortField: 'Name',
      sortDirection: 'Ascending'
    },
    templates: [],
    everbridgeTemplates: [],
    selectedTemplate: null,
    selectedTemplateToDelete: null,
    selectedTemplateToCopy: null,
    ui: {
      templateNewIsActive: false,
      templateCopyIsActive: false
    }
  },
  dataset: null
};

export const reducer = createReducer(
  initialState,
  //
  // Channels
  //
  on(commActions.getOrganizationChannelsFromApiComplete, (state, action) => {
    const { data, skip, take, totalCount, sortField, sortDirection } = action.data;
    return {
      ...state,
      organizationChannels: {
        ...state.organizationChannels,
        queryParams: {
          skip,
          take,
          totalCount,
          sortField,
          sortDirection
        },
        organizationChannels: data
      }
    };
  }),
  on(commActions.getConfiguredChannelsFromApiComplete, (state, action) => {
    const { data, skip, take, totalCount, sortField, sortDirection } = action.data;
    let selectedConfiguredChannel = state.configuredChannels.selectedConfiguredChannel;
    if (selectedConfiguredChannel) {
      selectedConfiguredChannel = data.find(cc => cc.uId === selectedConfiguredChannel.uId);
    }

    return {
      ...state,
      configuredChannels: {
        ...state.configuredChannels,
        queryParams: {
          skip,
          take,
          totalCount,
          sortField,
          sortDirection
        },
        configuredChannels: data,
        selectedConfiguredChannel,
        selectedConfiguredChannelToDelete: null
      }
    };
  }),
  on(commActions.editTopoEmailChannel, (state, action) => {
    const { channel } = action;

    return {
      ...state,
      configuredChannels: {
        ...state.configuredChannels,
        selectedConfiguredChannel: channel,
        ui: {
          ...state.configuredChannels.ui,
          topoEmailEditIsActive: true
        }
      }
    };
  }),
  on(commActions.editTwilioChannel, (state, action) => {
    const { channel } = action;

    return {
      ...state,
      configuredChannels: {
        ...state.configuredChannels,
        selectedConfiguredChannel: channel,
        ui: {
          ...state.configuredChannels.ui,
          twilioEditIsActive: true
        }
      }
    };
  }),
  on(commActions.editSendGridEmailChannel, (state, action) => {
    const { channel } = action;

    return {
      ...state,
      configuredChannels: {
        ...state.configuredChannels,
        selectedConfiguredChannel: channel,
        ui: {
          ...state.configuredChannels.ui,
          sendGridEmailEditIsActive: true
        }
      }
    };
  }),
  on(commActions.editEverbridgeChannel, (state, action) => {
    const { channel } = action;

    return {
      ...state,
      configuredChannels: {
        ...state.configuredChannels,
        selectedConfiguredChannel: channel,
        ui: {
          ...state.configuredChannels.ui,
          everbridgeEditIsActive: true
        }
      }
    };
  }),
  on(commActions.editSlackChannel, (state, action) => {
    const { channel } = action;

    return {
      ...state,
      configuredChannels: {
        ...state.configuredChannels,
        selectedConfiguredChannel: channel,
        ui: {
          ...state.configuredChannels.ui,
          slackEditIsActive: true
        }
      }
    };
  }),
  on(commActions.editMicrosoftTeamsChannel, (state, action) => {
    const { channel } = action;

    return {
      ...state,
      configuredChannels: {
        ...state.configuredChannels,
        selectedConfiguredChannel: channel,
        ui: {
          ...state.configuredChannels.ui,
          microsoftTeamsEditIsActive: true
        }
      }
    };
  }),
  on(commActions.clearSelectedConfiguredChannel, (state, action) => {
    return {
      ...state,
      configuredChannels: {
        ...state.configuredChannels,
        selectedConfiguredChannel: null,
        ui: {
          ...state.configuredChannels.ui,
          topoEmailEditIsActive: false,
          sendGridEmailEditIsActive: false,
          slackEditIsActive: false,
          everbridgeEditIsActive: false,
          microsoftTeamsEditIsActive: false,
          twilioEditIsActive: false,
        }
      }
    };
  }),
  on(commActions.setChannelToDelete, (state, action) => {
    const { channel } = action;
    return {
      ...state,
      configuredChannels: {
        ...state.configuredChannels,
        selectedConfiguredChannelToDelete: channel
      }
    };
  }),
  on(commActions.clearChannelToDelete, (state, action) => {
    return {
      ...state,
      configuredChannels: {
        ...state.configuredChannels,
        selectedConfiguredChannelToDelete: null
      }
    };
  }),
  on(commActions.updateSelectedConfiguredChannel, (state, action) => {
    return {
      ...state,
      configuredChannels: {
        ...state.configuredChannels,
        selectedConfiguredChannel: action.channel
      }
    };
  }),
  on(commActions.getSlackChannelsForConfiguredChannelFromApiComplete, (state, action) => {
    return {
      ...state,
      configuredChannels: {
        ...state.configuredChannels,
        slackChannelsForConfiguredChannel: action.channels,
        ui: {
          ...state.configuredChannels.ui,
          isLoading: false
        }
      }
    };
  }),
  on(commActions.disconnectFromSlackWithApiComplete, (state, action) => {
    const channels = [...state.configuredChannels.configuredChannels];
    const idx = channels.findIndex(c => c.uId === action.channel.uId);
    channels.splice(idx, 1, action.channel);

    return {
      ...state,
      configuredChannels: {
        ...state.configuredChannels,
        selectedConfiguredChannel: action.channel,
        configuredChannels: channels,
        slackChannelsForConfiguredChannel: null
      }
    };
  }),
  on(commActions.startGetSlackChannels, (state, action) => {
    return {
      ...state,
      configuredChannels: {
        ...state.configuredChannels,
        ui: {
          ...state.configuredChannels.ui,
          isLoading: true
        }
      }
    };
  }),
  on(commActions.displayChannelError, (state, action) => {
    return {
      ...state,
      configuredChannels: {
        ...state.configuredChannels,
        ui: {
          ...state.configuredChannels.ui,
          isLoading: false
        }
      }
    };
  }),

  //
  // Distribution Lists
  //
  on(commActions.newDistributionListComplete, (state, action) => {
    const { distributionList } = action;

    return {
      ...state,
      distributionLists: {
        ...state.distributionLists,
        selectedDistributionList: distributionList,
        ui: {
          ...state.distributionLists?.ui,
          distributionListNewIsActive: true
        }
      }
    };
  }),
  on(commActions.cancelNewDistributionList, (state, action) => {
    return {
      ...state,
      distributionLists: {
        ...state.distributionLists,
        selectedDistributionList: null,
        ui: {
          ...state.distributionLists?.ui,
          distributionListNewIsActive: false
        }
      }
    };
  }),
  on(commActions.getDistributionListsFromApiComplete, (state, action) => {
    const { data, skip, take, totalCount, sortField, sortDirection } = action.data;
    return {
      ...state,
      distributionLists: {
        ...state.distributionLists,
        queryParams: {
          skip,
          take,
          totalCount,
          sortField,
          sortDirection
        },
        distributionLists: data,
        selectedDistributionListToDelete: null,
        selectedDistributionList: null,
        ui: {
          ...state.distributionLists.ui,
          distributionListNewIsActive: false
        }
      }
    };
  }),
  on(commActions.saveDistributionListToApiComplete, (state, action) => {
    return {
      ...state,
      distributionLists: {
        ...state.distributionLists,
        selectedDistributionList: action.distributionList,
        dataQueryParams: {
          skip: 0,
          take: 10,
          totalCount: 0,
          sortField: null,
          sortDirection: 'Ascending'
        },
        ui: {
          ...state.distributionLists?.ui,
          distributionListNewIsActive: false
        }
      }
    };
  }),
  on(commActions.editDistributionListComplete, (state, action) => {
    return {
      ...state,
      distributionLists: {
        ...state.distributionLists,
        selectedDistributionList: action.distributionList,
        dataQueryParams: {
          skip: 0,
          take: 10,
          totalCount: 0,
          sortField: null,
          sortDirection: 'Ascending'
        }
      }
    };
  }),
  on(commActions.setDistributionListToDelete, (state, action) => {
    const { distributionList } = action;
    return {
      ...state,
      distributionLists: {
        ...state.distributionLists,
        selectedDistributionListToDelete: distributionList
      }
    };
  }),
  on(commActions.clearDistributionListToDelete, (state, action) => {
    return {
      ...state,
      distributionLists: {
        ...state.distributionLists,
        selectedDistributionListToDelete: null
      }
    };
  }),
  on(commActions.getDocumentsWithFilterFromApiComplete, (state, action) => {
    const { data, skip, take, totalCount } = action.results;

    const dataQueryParams = {
      ...state.distributionLists.dataQueryParams,
      skip,
      take,
      totalCount
    };

    return {
      ...state,
      distributionLists: {
        ...state.distributionLists,
        dataQueryParams,
        dataDocuments: data
      }
    };
  }),
  on(commActions.cancelEditDistributionListComplete, (state, action) => {
    return {
      ...state,
      distributionLists: {
        ...state.distributionLists,
        selectedDistributionList: null,
        selectedDistributionListToDelete: null,
        dataQueryParams: null,
        dataDocuments: null,
        dataset: null
      }
    };
  }),

  //
  // Templates
  //
  on(commActions.newTemplateComplete, (state, action) => {
    const { template } = action;

    return {
      ...state,
      templates: {
        ...state.templates,
        selectedTemplate: template,
        ui: {
          ...state?.templates?.ui,
          templateNewIsActive: true
        }
      }
    };
  }),
  on(commActions.cancelNewTemplate, (state, action) => {
    return {
      ...state,
      templates: {
        ...state.templates,
        selectedTemplate: null,
        ui: {
          ...state?.templates?.ui,
          templateNewIsActive: false
        }
      }
    };
  }),
  on(commActions.getTemplatesFromApiComplete, (state, action) => {
    const { data, skip, take, totalCount, sortField, sortDirection } = action.data;
    return {
      ...state,
      templates: {
        ...state.templates,
        queryParams: {
          skip,
          take,
          totalCount,
          sortField,
          sortDirection
        },
        templates: data,
        selectedTemplateToDelete: null,
        selectedTemplate: null,
        ui: {
          ...state?.templates?.ui,
          templateNewIsActive: false
        }
      }
    };
  }),
  on(commActions.saveTemplateToApiComplete, (state, action) => {
    return {
      ...state,
      templates: {
        ...state.templates,
        selectedTemplate: action.template,
        dataQueryParams: {
          skip: 0,
          take: 10,
          totalCount: 0,
          sortField: null,
          sortDirection: 'Ascending'
        },
        ui: {
          ...state?.templates?.ui,
          templateNewIsActive: false
        }
      }
    };
  }),
  on(commActions.editTemplateComplete, (state, action) => {
    return {
      ...state,
      templates: {
        ...state.templates,
        selectedTemplate: action.template,
        dataQueryParams: {
          skip: 0,
          take: 10,
          totalCount: 0,
          sortField: null,
          sortDirection: 'Ascending'
        }
      }
    };
  }),
  on(commActions.setTemplateToDelete, (state, action) => {
    const { template } = action;

    return {
      ...state,
      templates: {
        ...state.templates,
        selectedTemplateToDelete: template
      }
    };
  }),
  on(commActions.clearTemplateToDelete, (state, action) => {
    return {
      ...state,
      templates: {
        ...state.templates,
        selectedTemplateToDelete: null
      }
    };
  }),
  on(commActions.setTemplateToCopy, (state, action) => {
    const { template } = action;

    return {
      ...state,
      templates: {
        ...state.templates,
        selectedTemplateToCopy: template,
        ui: {
          ...state.templates.ui,
          templateCopyIsActive: true
        }
      }
    };
  }),
  on(commActions.clearTemplateToCopy, (state, action) => {
    return {
      ...state,
      templates: {
        ...state.templates,
        selectedTemplateToCopy: null,
        ui: {
          ...state.templates.ui,
          templateCopyIsActive: false
        }
      }
    };
  }),
  on(commActions.cancelEditTemplateComplete, (state, action) => {
    return {
      ...state,
      templates: {
        ...state.templates,
        selectedTemplate: null,
        selectedTemplateToDelete: null
      }
    };
  }),

  on(commActions.getEverbridgeTemplatesFromApiComplete, (state, action) => {
    return {
      ...state,
      templates: {
        ...state.templates,
        everbridgeTemplates: action.templates
      }
    };
  }),

  //
  // Datasets
  //
  on(commActions.getDatasetFromApiComplete, (state, action) => {
    const { dataset } = action;

    return {
      ...state,
      dataset
    };
  })
);

//
// Organization Channels
//
export const getOrganizationChannels = (state: State) => state?.organizationChannels?.organizationChannels;
export const getOrganizationChannelQueryParams = (state: State) => state?.organizationChannels?.queryParams;

//
// Configured Channels
//
export const getConfiguredChannels = (state: State) => state?.configuredChannels?.configuredChannels;
export const getConfiguredChannelQueryParams = (state: State) => state?.configuredChannels?.queryParams;
export const getSelectedConfiguredChannel = (state: State) => state?.configuredChannels.selectedConfiguredChannel;
export const getTopoEmailEditIsActive = (state: State) => state?.configuredChannels?.ui?.topoEmailEditIsActive;
export const getSendGridEmailEditIsActive = (state: State) => state?.configuredChannels?.ui?.sendGridEmailEditIsActive;
export const getEverbridgeEditIsActive = (state: State) => state?.configuredChannels?.ui?.everbridgeEditIsActive;
export const getSlackEditIsActive = (state: State) => state?.configuredChannels?.ui?.slackEditIsActive;
export const getMicrosoftTeamsEditIsActive = (state: State) => state?.configuredChannels?.ui?.microsoftTeamsEditIsActive;
export const getSelectedConfiguredChannelToDelete = (state: State) => state?.configuredChannels?.selectedConfiguredChannelToDelete;
export const getSlackChannelsForConfiguredChannel = (state: State) => state?.configuredChannels?.slackChannelsForConfiguredChannel;
export const getIsLoading = (state: State) => state?.configuredChannels?.ui?.isLoading;
export const getTwilioEditIsActive = (state: State) => state?.configuredChannels?.ui?.twilioEditIsActive;

//
// Distribution Lists
//
export const getDistributionLists = (state: State) => state?.distributionLists?.distributionLists;
export const getDistributionListsQueryParams = (state: State) => state?.distributionLists?.queryParams;
export const getDistributionListNewIsActive = (state: State) => state?.distributionLists?.ui?.distributionListNewIsActive;
export const getSelectedDistributionList = (state: State) => state?.distributionLists?.selectedDistributionList;
export const getSelectedDistributionListToDelete = (state: State) => state?.distributionLists?.selectedDistributionListToDelete;
export const getDistributionListDataQueryParams = (state: State) => state?.distributionLists?.dataQueryParams;
export const getDistributionListDataDocuments = (state: State) => state?.distributionLists?.dataDocuments;

//
// Templates
//
export const getTemplates = (state: State) => state?.templates?.templates;
export const getTemplateQueryParams = (state: State) => state?.templates?.queryParams;
export const getTemplateNewIsActive = (state: State) => state?.templates?.ui?.templateNewIsActive;
export const getSelectedTemplate = (state: State) => state?.templates?.selectedTemplate;
export const getSelectedTemplateToDelete = (state: State) => state?.templates?.selectedTemplateToDelete;
export const getTemplateCopyIsActive = (state: State) => state?.templates?.ui?.templateCopyIsActive;
export const getSelectedTemplateToCopy = (state: State) => state?.templates?.selectedTemplateToCopy;
export const getEverbridgeTemplates = (state: State) => state?.templates?.everbridgeTemplates;

//
// Communication Center - Dataset
//
export const getCommunicationCenterDataset = (state: State) => state?.dataset;
